import { Camera, CheckCircle2, XCircle, X, Ticket, Package, User, CalendarDays, Gift, Clock } from "lucide-react";

const fmtDay = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });

const NON_VEG = /non[\s-]?veg|chicken|mutton|fish|meat|egg|prawn|beef|pork|lamb|seafood/i;
const VEG = /\bveg|vegan|paneer/i;
const PALETTE = ["bg-purple-600", "bg-amber-500", "bg-sky-600", "bg-pink-600", "bg-teal-600", "bg-orange-600", "bg-indigo-600"];
export const variantColor = (name = "") => {
  if (NON_VEG.test(name)) return "bg-red-600";
  if (VEG.test(name)) return "bg-green-600";
  let h = 0;
  for (const c of name.toLowerCase()) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

function TicketHighlight({ r, isValid }) {
  if (!r?.tier_name) return null;
  return (
    <div className={`rounded-2xl border-2 p-4 ${isValid ? "border-green-500 bg-green-50" : "border-red-300 bg-red-50"}`} data-testid="scan-ticket-highlight">
      {r.package_name && (
        <p className="text-xs font-semibold text-slate-600 flex items-center gap-1 mb-1" data-testid="scan-package-name"><Package className="w-3.5 h-3.5" /> {r.package_name}</p>
      )}
      <p className="text-2xl font-extrabold text-[#1e3a5f] leading-tight flex items-start gap-2" data-testid="scan-tier-name">
        <Ticket className="w-6 h-6 mt-0.5 shrink-0" /> {r.tier_name}
      </p>
      {r.variant_name && (
        <span className={`inline-block mt-2 px-3 py-1 rounded-full ${variantColor(r.variant_name)} text-white text-sm font-bold`} data-testid="scan-variant-name">{r.variant_name}</span>
      )}
    </div>
  );
}

function Row({ icon: Icon, children, testid }) {
  return <div className="flex items-start gap-2 text-sm text-slate-700" data-testid={testid}><Icon className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" /><div className="min-w-0">{children}</div></div>;
}

function Details({ r }) {
  return (
    <div className="space-y-2">
      {r.attendee_name && (
        <Row icon={User} testid="scan-attendee">
          <span className="font-semibold text-[#1e3a5f]">{r.attendee_name}</span>
          {r.buyer_name && r.buyer_name !== r.attendee_name && <span className="block text-xs text-slate-500">Purchased by {r.buyer_name}</span>}
        </Row>
      )}
      {r.status === "used" && r.used_at_local && (
        <Row icon={Clock} testid="scan-used-at"><span className="font-semibold text-red-600">Checked in {r.used_at_local}</span>{r.used_by && <span className="block text-xs text-slate-500">by {r.used_by}</span>}</Row>
      )}
      {r.event_name && <Row icon={CalendarDays} testid="scan-event-name">
        {r.event_name}
        {r.valid_dates?.length > 0 && <span className="block text-xs text-slate-500">Valid: {r.valid_dates.map(fmtDay).join(", ")}</span>}
      </Row>}
      {r.valid && r.on_attend_credit_amount > 0 && (
        <Row icon={Gift} testid="scan-credit-hint"><span className="text-green-700 font-semibold">€{r.on_attend_credit_amount.toFixed(2)} on-attend credit will be issued</span></Row>
      )}
      {r.ticket_id && <p className="text-[10px] text-slate-400 font-mono">ID {r.ticket_id.slice(0, 8)}</p>}
    </div>
  );
}

export default function ScanResultOverlay({ result, confirming, onMark, onClose, onScanAnother }) {
  const isValid = !!result?.valid;
  const Icon = isValid ? CheckCircle2 : XCircle;
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-3" data-testid={`scan-result-${isValid ? "valid" : "invalid"}`}>
      <div className="bg-white rounded-2xl overflow-hidden w-full max-w-md max-h-[95vh] overflow-y-auto">
        <div className={`${isValid ? "bg-green-500" : "bg-red-500"} text-white p-5 text-center relative`}>
          <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30" aria-label="Close" data-testid="scan-result-x-btn"><X className="w-5 h-5" /></button>
          <Icon className="w-14 h-14 mx-auto mb-1" strokeWidth={2.5} />
          <p className="text-3xl font-extrabold" data-testid="scan-result-status">{isValid ? "VALID" : "INVALID"}</p>
          <p className="text-sm opacity-90 mt-1" data-testid="scan-result-message">{result?.message || ""}</p>
        </div>
        <div className="p-4 space-y-4">
          <TicketHighlight r={result} isValid={isValid} />
          <Details r={result || {}} />
        </div>
        <div className="p-3 border-t bg-slate-50 grid grid-cols-2 gap-2">
          {isValid && (
            <button onClick={onMark} disabled={confirming} data-testid="mark-attendance-btn"
              className="col-span-2 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl py-3.5 disabled:opacity-50 flex items-center justify-center gap-2">
              <CheckCircle2 className="w-5 h-5" /> {confirming ? "Marking…" : "Mark Attendance"}
            </button>
          )}
          {!isValid && (
            <button onClick={onScanAnother} data-testid="scan-another-btn"
              className="col-span-2 bg-[#1e3a5f] hover:bg-[#16304f] text-white font-bold rounded-xl py-3.5 flex items-center justify-center gap-2">
              <Camera className="w-5 h-5" /> Scan Another
            </button>
          )}
          <button onClick={onClose} className="col-span-2 bg-slate-200 text-slate-700 font-semibold rounded-xl py-3" data-testid="scan-close-btn">Close</button>
        </div>
      </div>
    </div>
  );
}
